/**
 * Словарь генерации видео у партнёра (Musebox API v2).
 *
 * Здесь только КОНСТАНТЫ, общие для базы, конфига и клиента API: их читают
 * и `apps/api/src/db/schema.ts` (перечисление колонки), и схема конфига
 * ниже по файлу `config.ts`, и модуль `apps/api/src/musebox/*`.
 * Разбор ответов партнёра живёт не здесь, а в `musebox/protocol.ts` —
 * это чужой контракт, и ему незачем быть в общем пакете.
 */

/**
 * Жизненный цикл нашей задачи на генерацию.
 *
 * Пять состояний вместо четырёх из ТЗ §3, и лишнее здесь одно — `ready`.
 * Оно отделяет «партнёр отчитался о доставке» от «файл у нас, проверен
 * и виден игроку». Разница не косметическая: правило №5 запрещает отдавать
 * игроку чужую ссылку хотлинком, и на v2 партнёр её и не даёт — он сам
 * кладёт файл в наш приёмный префикс `incoming/` и присылает квитанцию
 * (`delivery.status: "delivered"`). Пока размер не сверен с квитанцией
 * и файл не скопирован на боевой путь, сцена не играет. Слив `ready`
 * и `stored` в одно состояние значило бы считать задачу завершённой раньше,
 * чем ролик действительно доступен, — и узнать правду от игрока, увидевшего
 * пустую сцену.
 *
 *   queued  — строка создана у нас, запрос партнёру ещё не ушёл
 *   running — задача принята партнёром (их `queued` и `processing`)
 *   ready   — партнёр отчитался о доставке, файл лежит в incoming/,
 *             но ещё не сверен и не скопирован на боевой путь
 *   stored  — файл на bunny, строка `media_assets` записана: сцена играет
 *   failed  — окончательный отказ; при провале доставки кредиты возвращает партнёр (`refunded`)
 */
export const GENERATION_STATUSES = ['queued', 'running', 'ready', 'stored', 'failed'] as const
export type GenerationStatus = (typeof GENERATION_STATUSES)[number]

/** Статусы, при которых задача ещё чего-то ждёт. Их считает предохранитель очереди. */
export const ACTIVE_GENERATION_STATUSES = ['queued', 'running', 'ready'] as const

/**
 * Тир качества. Влияет на длинную сторону кадра и на цену
 * (`pricing.qualityMultipliers` в их `/api/v2/account`).
 *
 * Множители НЕ ЗАШИТЫ у нас нигде намеренно: их правит администратор
 * партнёра без релиза, и наша копия разошлась бы молча. Цену конкретного
 * заказа спрашиваем у `POST /api/v2/jobs/estimate`.
 */
export const MUSEBOX_QUALITY_TIERS = ['fast', 'normal', 'medium', 'hd'] as const
export type MuseboxQuality = (typeof MUSEBOX_QUALITY_TIERS)[number]

/**
 * Длительность ролика в секундах. 5 поддерживают все шаблоны, 7 и 10 —
 * только те, у которых стоят флаги `duration7sEnabled`/`duration10sEnabled`.
 * Просить 10 у шаблона без флага — это `400` уже после списания решения,
 * поэтому длительность проверяется по каталогу до отправки.
 */
export const MUSEBOX_DURATIONS = [5, 7, 10] as const
export type MuseboxDuration = (typeof MUSEBOX_DURATIONS)[number]

/** Виды задач партнёра. Нам нужен `i2v`: фото героини → ролик. */
export const MUSEBOX_JOB_TYPES = ['i2v', 'extend', 't2i'] as const
export type MuseboxJobType = (typeof MUSEBOX_JOB_TYPES)[number]

/** Статусы задачи на стороне партнёра (§7 их документации). */
export const MUSEBOX_JOB_STATUSES = ['queued', 'processing', 'done', 'failed'] as const
export type MuseboxJobStatus = (typeof MUSEBOX_JOB_STATUSES)[number]

/**
 * Параметры одной генерации — то, что лежит в `generation_jobs.params`
 * и в точности повторяет тело `POST /api/v2/jobs`.
 *
 * Хранится целиком и рядом с результатом ради одного вопроса, который
 * задают всегда: «почему этот ролик получился не таким». Ответ на него —
 * это шаблон, качество, длительность и промпт на момент отправки, а не те,
 * что стоят в конфиге сегодня.
 */
export interface GenerationParams {
  type: MuseboxJobType
  quality: MuseboxQuality
  duration: MuseboxDuration
  addAudio: boolean
  /** Только для шаблонов с `needPrompt: true`. */
  prompt?: string
}
